import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { AuthContext } from "@hub/auth";
import { FULL_TOUR, type TourScreen, type TourStep } from "./tour";
import { hasSeenTour, markTourSeen } from "./seen";

// Tour state + the navigation seam.
//
// The prototype could drive navigation from one `tourNav` function because its
// whole app was a single component holding `view`, `auditBatchId` and
// `auditCaseId` in local state. Here the audit screens are separate view
// primitives mounted into a blade stack, and only the MOUNTED one holds the
// context needed to open its child.
//
// So navigation is distributed instead: the tour asks for a screen and the
// mounted views answer, innermost first. Opening a batch swaps the mounted view,
// the new one registers its own handler, and the next request is served by it.
// Anything no view can reach — the program dashboard is a sibling STACK — falls
// through to the shell, which can. That composes to the same crossing-screens
// walk without any component needing to know the whole map.
//
// None of it ran until now: useTourNavigator had no callers at all, so every
// step's `nav` was a no-op and the tour narrated four screens from the queue.

interface TourState {
  /** The step being shown, or null when the tour is closed. */
  step: TourStep | null;
  index: number;
  total: number;
  isOpen: boolean;

  start: () => void;
  close: () => void;
  next: () => void;
  back: () => void;

  /**
   * Registers a view's ability to navigate. Returns an unregister function.
   *
   * Navigators form a CHAIN, tried innermost-first. The most recently mounted
   * view is the deepest and gets first refusal; anything it declines falls
   * through to the ones above it. Last-wins alone is not enough — the program
   * dashboard is a sibling STACK that only the shell can swap to, so a step
   * asking for it can never be served by the mounted audit view, and with a
   * single handler those steps reached nobody at all.
   */
  registerNavigator: (fn: TourNavigator) => () => void;
}

/**
 * Handles a request to reach `screen`. Return true if this view handled it (or
 * is already there), false if it cannot.
 *
 * A false return is not a failure — it means "not my screen", and the request
 * passes to the next navigator out. When nobody can serve it the tour still
 * advances and the step renders centred without a spotlight, which is strictly
 * better than stalling on a step whose anchor will never appear.
 */
export type TourNavigator = (screen: TourScreen) => boolean;

const TourContext = createContext<TourState | null>(null);

/**
 * The signed-in user's id, or null when there is no AuthProvider above us.
 *
 * Reads the context directly instead of calling useAuth, which THROWS when
 * unmounted from a provider. WorkbenchShell — and therefore this provider — is
 * also mounted in the backendless preview and in tests, and help content must
 * never be the reason a screen crashes. Same reasoning as useAuditTour below.
 */
function useOptionalUserId(): string | null {
  const auth = useContext(AuthContext);
  return auth?.user?.id ?? null;
}

export function AuditTourProvider({ children }: { children: ReactNode }) {
  const [index, setIndex] = useState<number | null>(null);
  // Innermost last, so the chain is walked from the end.
  const navigatorsRef = useRef<TourNavigator[]>([]);
  const userId = useOptionalUserId();
  const autoStarted = useRef(false);

  const registerNavigator = useCallback((fn: TourNavigator) => {
    navigatorsRef.current = [...navigatorsRef.current, fn];
    return () => {
      // Remove THIS handler by identity, wherever it now sits. A view unmounting
      // after its replacement registered must not disturb the newer one — that
      // ordering happens on every navigation, and clobbering it would leave the
      // tour unable to move.
      navigatorsRef.current = navigatorsRef.current.filter((n) => n !== fn);
    };
  }, []);

  /** Walks the chain innermost-first, stopping at the first navigator that
   *  handles the screen. */
  const navigateTo = useCallback((screen: TourScreen) => {
    const chain = navigatorsRef.current;
    for (let i = chain.length - 1; i >= 0; i--) {
      if (chain[i](screen)) return;
    }
  }, []);

  const isOpen = index !== null;
  const step = index === null ? null : (FULL_TOUR[index] ?? null);

  // Ask the mounted view to reach this step's screen. Runs on step change
  // rather than inside next()/back() so it also fires for the first step and
  // for a tour restarted from the menu.
  useEffect(() => {
    if (!step) return;
    navigateTo(step.nav);
  }, [step, navigateTo]);

  const start = useCallback(() => setIndex(0), []);

  // Closing marks it seen — on dismissal, not on completion.
  //
  // Marking only at the last step means anyone who presses Esc gets the tour
  // again at every sign-in, which trains people to dismiss it faster. Someone
  // who wanted it can replay from the Help menu; someone who did not should be
  // asked once.
  const close = useCallback(() => {
    setIndex(null);
    markTourSeen(userId);
  }, [userId]);

  // First sign-in starts the tour by itself. Everything below was already built
  // and mounted; nothing ever triggered it, so it only ran for a user who
  // happened to open the Help menu.
  //
  // Guarded by a ref rather than by `isOpen` so it fires at most once per mount:
  // keyed on isOpen it would restart the moment the user closed it. Requires a
  // userId, so the preview and tests never auto-start — there is no principal to
  // remember, and a tour that reappears on every reload is worse than none.
  useEffect(() => {
    if (autoStarted.current || !userId) return;
    autoStarted.current = true;
    if (!hasSeenTour(userId)) start();
  }, [userId, start]);
  const next = useCallback(
    () =>
      setIndex((i) =>
        i === null ? null : i + 1 >= FULL_TOUR.length ? null : i + 1,
      ),
    [],
  );
  const back = useCallback(
    () => setIndex((i) => (i === null || i === 0 ? i : i - 1)),
    [],
  );

  // Esc closes. A modal overlay with no keyboard exit is a trap, and this one
  // covers the whole screen including the reviewer's work.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, close]);

  const value = useMemo<TourState>(
    () => ({
      step,
      index: index ?? 0,
      total: FULL_TOUR.length,
      isOpen,
      start,
      close,
      next,
      back,
      registerNavigator,
    }),
    [step, index, isOpen, start, close, next, back, registerNavigator],
  );

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
}

/**
 * Tour state. Returns null outside the provider rather than throwing: the audit
 * views are also mounted in tests and in the backendless preview, and a missing
 * tour must degrade to "no help button", never to a crashed screen.
 */
export function useAuditTour(): TourState | null {
  return useContext(TourContext);
}

/**
 * Registers this view as able to serve tour navigation while it is mounted.
 *
 * `handler` is held in a ref so a view can pass an inline closure over fresh
 * props without re-registering on every render — re-registering in an effect
 * that depends on the handler identity would thrash the navigator on each
 * keystroke in the batch search box.
 */
export function useTourNavigator(handler: TourNavigator): void {
  const tour = useAuditTour();
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  const register = tour?.registerNavigator;
  useEffect(() => {
    if (!register) return;
    return register((screen) => handlerRef.current(screen));
  }, [register]);
}
